/**
 * src/pages/VerifyEmail.js
 * Magic link landing page — the user clicks a link in their email and arrives here.
 * Reads the token from the URL, calls the backend to verify, and shows the result.
 * Public route (no JWT required — user may not have an active session).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user, verifyEmail, resendVerification } = useAuth();

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [errorCode, setErrorCode] = useState('');   // token_expired | token_used | invalid_token
  const [errorMessage, setErrorMessage] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // '' | sending | sent | error
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorCode('invalid_token');
      setErrorMessage('Invalid verification link.');
      return;
    }

    // Prevent double-call in React strict mode
    if (calledRef.current) return;
    calledRef.current = true;

    async function verify() {
      try {
        await api.post('/portal/auth/verify-email', { token });
        // Update local auth state if user is logged in
        if (user) {
          verifyEmail();
        }
        setStatus('success');
      } catch (err) {
        const data = err.response?.data || {};
        setStatus('error');
        setErrorCode(data.error || 'invalid_token');
        setErrorMessage(data.message || 'Could not verify your email. Please try again.');
      }
    }

    verify();
  }, [token, user, verifyEmail]);

  async function handleResend() {
    setResendStatus('sending');
    try {
      await resendVerification();
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('error');
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--content-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
          <div style={{
            width: '36px', height: '36px',
            background: 'var(--accent)',
            borderRadius: '8px'
          }} />
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Process Trails
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Client Portal
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>

            {/* Loading */}
            {status === 'loading' && (
              <>
                <div style={{
                  width: '48px', height: '48px',
                  border: '3px solid var(--border)',
                  borderTopColor: 'var(--accent)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 20px'
                }} />
                <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  Verifying your email…
                </h1>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  This will only take a moment.
                </p>
              </>
            )}

            {/* Success */}
            {status === 'success' && (
              <>
                <div style={{
                  width: '48px', height: '48px',
                  background: '#d1fae5',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  <span role="img" aria-label="checkmark" style={{ color: '#059669' }}>&#10003;</span>
                </div>
                <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  Email verified!
                </h1>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                  Your email address has been confirmed. You can now access the full portal.
                </p>
                {user ? (
                  <button
                    className="btn btn-primary w-full"
                    onClick={() => navigate('/dashboard')}
                    style={{ justifyContent: 'center', padding: '10px' }}
                  >
                    Continue to Dashboard
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="btn btn-primary w-full"
                    style={{ justifyContent: 'center', padding: '10px', textDecoration: 'none', display: 'block' }}
                  >
                    Sign in
                  </Link>
                )}
              </>
            )}

            {/* Error */}
            {status === 'error' && (
              <>
                <div style={{
                  width: '48px', height: '48px',
                  background: errorCode === 'token_expired' ? '#fef3c7' : '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  <span style={{ color: errorCode === 'token_expired' ? '#d97706' : '#dc2626' }}>!</span>
                </div>
                <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  {errorCode === 'token_expired' ? 'Link expired' :
                   errorCode === 'token_used' ? 'Already verified' :
                   'Verification failed'}
                </h1>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                  {errorMessage}
                </p>

                {/* Resend option for expired tokens (only if logged in) */}
                {errorCode === 'token_expired' && user && (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      className="btn btn-primary w-full"
                      onClick={handleResend}
                      disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                      style={{ justifyContent: 'center', padding: '10px' }}
                    >
                      {resendStatus === 'sending' ? 'Sending…' :
                       resendStatus === 'sent' ? 'Email sent!' :
                       'Resend verification email'}
                    </button>
                    {resendStatus === 'sent' && (
                      <p style={{ fontSize: '12px', color: 'var(--success)', marginTop: '8px' }}>
                        Check your inbox for the new verification link.
                      </p>
                    )}
                  </div>
                )}

                <Link
                  to="/login"
                  style={{
                    fontSize: '13px',
                    color: 'var(--accent)',
                    textDecoration: 'none'
                  }}
                >
                  {errorCode === 'token_expired' && !user ? 'Sign in to request a new link' : 'Back to sign in'}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
